import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { useOnboardingProgressQuery } from 'src/api/queries/onboardingQueries';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { useUpdateUserPreferencesMutation } from 'src/api/mutations/onboardingMutations';
import { useAssetSchemasListQuery } from 'src/api/queries/schemesQueries';
import { useTemplatesListQuery } from 'src/api/queries/templateQueries';
import { useUserManagementQuery } from 'src/api/queries/userManagementQueries';
import { useAllCollectionsQuery } from 'src/api/queries/collectionsQueries';
import { useSharesListQuery } from 'src/api/queries/shareQueries';
import { OnboardingChecklist } from 'src/types/TOnboarding';
import useFileSpinPicker from 'src/hooks/useFileSpinPicker';
import useModal from 'src/hooks/useModal';
import BaseModal from 'src/components/BaseModal';
import AIWorkflowGuide from 'src/components/AIWorkflowGuide';
import { FeatureEmptyGuideCard } from 'src/components/FeatureEmptyGuideCard';
import styles from 'src/pages/Onboarding/styles.module.scss';

type ChecklistItemConfig = {
  key: keyof OnboardingChecklist;
  label: string;
  link?: string;
  action?: string;
};

const ADMIN_ITEMS: ChecklistItemConfig[] = [
  { key: 'account_setup', label: 'Set up your account', link: '/me' },
  { key: 'schemas_added', label: 'Add asset schemas', link: '/admin/schemas' },
  { key: 'share_presets_configured', label: 'Set up share templates', link: '/admin/shares/templates' },
  { key: 'ai_workflow_completed', label: 'Complete an AI workflow', action: 'open_ai_guide' },
  { key: 'first_asset_uploaded', label: 'Add an asset', action: 'open_asset_guide' },
  { key: 'first_collection_created', label: 'Create a collection', action: 'open_collection_guide' },
  { key: 'team_member_invited', label: 'Invite a team member', link: '/admin/users' },
  { key: 'branding_configured', label: 'Configure branding', link: '/admin/settings/watermark' },
  { key: 'first_share_created', label: 'Create a share', action: 'open_share_guide' },
];

const MANAGER_CREATOR_ITEMS: ChecklistItemConfig[] = [
  { key: 'account_setup', label: 'Set up your account', link: '/me' },
  { key: 'share_presets_configured', label: 'Set up share templates', link: '/shares/templates' },
  { key: 'first_asset_uploaded', label: 'Add an asset', action: 'open_asset_guide' },
  { key: 'first_collection_created', label: 'Create a collection', action: 'open_collection_guide' },
  { key: 'first_share_created', label: 'Create a share', action: 'open_share_guide' },
];

const USER_ITEMS: ChecklistItemConfig[] = [
  { key: 'account_setup', label: 'Set up your account', link: '/me' },
  { key: 'first_asset_uploaded', label: 'Add an asset', action: 'open_picker' },
  { key: 'first_collection_created', label: 'Create a collection', action: 'open_collection_guide' },
  { key: 'first_share_created', label: 'Create a share', action: 'open_share_guide' },
];

type GettingStartedChecklistProps = {
  totalAssets?: number;
};

const GettingStartedChecklist = ({ totalAssets }: GettingStartedChecklistProps) => {
  const auth = useAuthUser();
  const authenticatedUser = auth();
  const role = authenticatedUser?.role;
  const isAdmin = role === 'ADMIN';
  const isUser = role === 'USER';
  const isManagerOrCreator = role === 'MANAGER' || role === 'CREATOR';

  const { data: progress, isLoading } = useOnboardingProgressQuery();
  const dismissMutation = useUpdateOnboardingProgressMutation();
  const preferencesMutation = useUpdateUserPreferencesMutation();
  const { uploadContent } = useFileSpinPicker();
  const { showModal, closeModal } = useModal();
  const { data: schemasResult } = useAssetSchemasListQuery('ACTIVE', undefined, { enabled: isAdmin });
  const { data: templatesResult } = useTemplatesListQuery(undefined, { enabled: isAdmin || isManagerOrCreator });
  const { data: usersResult } = useUserManagementQuery({ page: 1 }, undefined, { enabled: isAdmin });
  const { data: collectionsResult } = useAllCollectionsQuery(undefined);
  const { data: sharesResult } = useSharesListQuery(undefined, undefined, { enabled: !isUser });

  const [collapsed, setCollapsed] = useState(false);

  if (isLoading || !progress) return null;
  if (progress.dismissed) return null;

  const checklist = progress.checklist || {};

  const items = isAdmin
    ? ADMIN_ITEMS
    : isManagerOrCreator
      ? MANAGER_CREATOR_ITEMS
      : USER_ITEMS.filter((item) => !(isUser && item.key === 'first_share_created'));

  const schemas = schemasResult?.data ?? [];
  const templates = templatesResult?.data?.templates ?? [];
  const nonDefaultSchemaCount = schemas.filter((s: any) => s.id !== 0).length;
  const nonDefaultTemplateCount = templates.filter((t: any) => !t.is_default).length;

  const totalUsers = usersResult?.total_users ?? 0;
  const totalCollections = collectionsResult?.data?.collections?.length ?? 0;
  const totalShares = sharesResult?.data?.shares?.length ?? 0;

  const isItemDone = (key: keyof OnboardingChecklist) => {
    if (key === 'first_asset_uploaded') {
      if (checklist.first_asset_uploaded) return true;
      if (!checklist.demo_loaded && totalAssets !== undefined && totalAssets > 0) return true;
      return false;
    }
    if (key === 'schemas_added' && nonDefaultSchemaCount > 0) return true;
    if (key === 'share_presets_configured' && nonDefaultTemplateCount > 0) return true;
    if (key === 'team_member_invited' && totalUsers > 1) return true;
    if (key === 'first_collection_created') return totalCollections > 0;
    if (key === 'first_share_created') return totalShares > 0;
    if (key === 'branding_configured') {
      return !!checklist[key];
    }
    return !!checklist[key];
  };

  const completedCount = items.filter((item) => isItemDone(item.key)).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleDismiss = () => {
    dismissMutation.mutate({ dismissed: true });
    preferencesMutation.mutate({ checklist_dismissed: true });
  };

  return (
    <div className={styles.checklistWidget}>
      <div className={styles.checklistHeader} onClick={() => setCollapsed(!collapsed)}>
        <div className={styles.checklistHeaderLeft}>
          <h3>Getting Started</h3>
          <div className={styles.checklistProgressBar}>
            <div style={{ width: `${progressPercent}%` }} />
          </div>
          <span className={styles.checklistProgress}>
            {completedCount} of {totalCount} complete
          </span>
        </div>
        <div className={styles.checklistHeaderActions}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              color: 'rgba(1, 10, 44, 0.4)',
            }}
          >
            Dismiss
          </button>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'rgba(1, 10, 44, 0.3)' }}>
            {collapsed ? '▸' : '▾'}
          </span>
        </div>
      </div>

      {!collapsed && (
        <div className={styles.checklistBody}>
          {items.map((item) => {
            const done = isItemDone(item.key);
            return (
              <div
                key={item.key}
                className={`${styles.checklistItem} ${done ? styles.checklistItemDone : ''}`}
              >
                <div className={`${styles.checklistCheck} ${done ? styles.checklistCheckDone : ''}`}>
                  {done && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                {item.action === 'open_picker' ? (
                  <button
                    type="button"
                    onClick={() => uploadContent()}
                    className={styles.checklistActionButton}
                  >
                    {item.label}
                  </button>
                ) : item.action === 'open_ai_guide' ? (
                  <button
                    type="button"
                    onClick={() =>
                      showModal(
                        <BaseModal containerStyle={{ maxWidth: '640px', width: '90%' }}>
                          <AIWorkflowGuide
                            userEmail={authenticatedUser?.userEmail}
                            onComplete={() => closeModal()}
                            onClose={() => closeModal()}
                          />
                        </BaseModal>,
                      )
                    }
                    className={styles.checklistActionButton}
                  >
                    {item.label}
                  </button>
                ) : item.action === 'open_asset_guide' ? (
                  <button
                    type="button"
                    onClick={() =>
                      showModal(
                        <BaseModal containerStyle={{ maxWidth: '640px', width: '90%' }}>
                          <FeatureEmptyGuideCard
                            info="Upload images, videos, documents and other files to your asset library."
                            mediaStep={{
                              src: '/userhelp/add-assets.jpg',
                              alt: 'Assets page showing the Add Assets button',
                              body: <>Go to the <Link to="/assets" onClick={() => closeModal()}>Assets page</Link> and click the <strong>Add Assets</strong> button to upload files from your computer, URL, or cloud storage.</>,
                            }}
                          />
                        </BaseModal>,
                      )
                    }
                    className={styles.checklistActionButton}
                  >
                    {item.label}
                  </button>
                ) : item.action === 'open_collection_guide' ? (
                  <button
                    type="button"
                    onClick={() =>
                      showModal(
                        <BaseModal containerStyle={{ maxWidth: '640px', width: '90%' }}>
                          <FeatureEmptyGuideCard
                            info="Collections let you organise assets into groups. Private collections are visible only to you; group collections are shared with your team."
                            mediaStep={{
                              src: '/userhelp/create-collection.jpg',
                              alt: 'Assets page showing the With Selected menu with Create new Collection option',
                              body: <>To create a collection, select assets on the <Link to="/assets" onClick={() => closeModal()}>Assets page</Link> and use the <strong>With Selected</strong> dropdown to add them to a new collection or an existing collection.</>,
                            }}
                          />
                        </BaseModal>,
                      )
                    }
                    className={styles.checklistActionButton}
                  >
                    {item.label}
                  </button>
                ) : item.action === 'open_share_guide' ? (
                  <button
                    type="button"
                    onClick={() =>
                      showModal(
                        <BaseModal containerStyle={{ maxWidth: '640px', width: '90%' }}>
                          <FeatureEmptyGuideCard
                            info="Shares let you publish a branded page of selected assets with optional password protection, approvals, and expiry."
                            mediaStep={{
                              src: '/userhelp/create-share.jpg',
                              alt: 'Assets page showing the With Selected menu with Add to Share option highlighted',
                              body: <>Select assets on the <Link to="/assets" onClick={() => closeModal()}>Assets page</Link>, in a collection, or in your basket, then use <strong>With Selected</strong> → <strong>Share</strong> to create a new share or add to an existing one.</>,
                            }}
                          />
                        </BaseModal>,
                      )
                    }
                    className={styles.checklistActionButton}
                  >
                    {item.label}
                  </button>
                ) : item.link?.startsWith('http') ? (
                  <a href={item.link} target="_blank" rel="noopener noreferrer">{item.label}</a>
                ) : item.link ? (
                  <Link to={item.link}>{item.label}</Link>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GettingStartedChecklist;
